// Comparison-basis resolver — pure functions over Target / Reading
// (docs/plans/plant-model-construction.md § 9.3: "target rows take priority
// over design once they exist" is an authority rule, not a per-tile choice).
// No React, no seed imports, no mutation. Every landing triad, report header
// and KPI pill reads the figure it is judged against through here and labels
// the basis it got (construct.R15); nothing else decides which of design,
// ramp or the M5 track a number is compared to. Contract frozen by the
// orchestrator before wave 2 of the route-reads run — consumers do not
// redefine it; gaps go in the build report.

import type { Reading } from '../schema/reading';
import type { KpiPeriod, Target, TargetBasis } from '../schema/target';

export type ComparisonBasisAuthority = {
  /** Strongest first. A basis absent from the ranking is dormant: a seeded
   * row for it never wins, so the M5 track can sit in the seed unconsumed. */
  ranking: readonly TargetBasis[];
};

/** The mock's authority. `pl.D1` (#142) ruled 2026-09-06 (Miles — no client
 * ruling is coming; Bunker Hill is a prospect, not a client; Paul's M4 §7 /
 * C1 Appendix B stand as the basis) — `budget` now enters at the head.
 * `stretch` and `forecast` stay unranked (dormant): no consumer resolves
 * them and no row asks to be seeded against them yet.
 *
 * Round 1 (2026-09-09, same #142) shipped the ranking flip alone and found
 * it a no-op: every `Reading` in the seed carried `targetBasis: 'ramp'`
 * (never `'budget'`) and the `TARGETS` array every landing/report call site
 * passes in (`bunker-hill-advisory-seed.ts`) seeded only `'design'` and
 * `'ramp'` rows, so no call site had a `budget` candidate for this ranking
 * to promote. See `docs/plans/route-reads/reviews/planning-flip-build-report.md`
 * for that finding in full.
 *
 * Round 2 (2026-09-09, #142, orchestrator ruling) closed the gap at the
 * data layer instead of the ranking: there is exactly one Month-4 target
 * level at this plant — Paul's FD ramp figure — so the nine `targetBasis:
 * 'ramp'` readings (`bunker-hill-readings.ts`) and the six `basis: 'ramp'`
 * rows in `TARGETS` (`bunker-hill-advisory-seed.ts`) were relabelled to
 * `'budget'`. No value moved; each relabelled row's `provenance` still says
 * it is Paul's Month-4 ramp figure, now carried as the Budget track. With
 * that done, `ranking: ['budget', 'ramp', 'design']` does exactly what
 * round 1 documented it as unable to do: every call site that used to
 * resolve `'ramp'` now resolves `'budget'` against the identical number.
 *
 * What is ranked and live today: `'budget'` — every seed row that used to
 * read `'ramp'`. What is ranked and dormant: `'ramp'` itself — no seed row
 * is literally tagged `'ramp'` any more, but `readingComparisonBasis` below
 * still defaults an untagged `target` to `'ramp'` (the seed's long-standing
 * convention for a `Reading` with a bare `target` and no `targetBasis`), so
 * `'ramp'` stays ranked, second, ahead of `'design'`, to preserve that
 * convention for the day a reading is seeded that way again — it would
 * still outrank a design fallback, per the original contract's intent.
 * `'design'` stays last: the fallback when neither a budget-tagged nor
 * ramp-tagged row exists for a KPI/period.
 * See `docs/plans/route-reads/reviews/planning-flip-build-report.md` § Round 2. */
export const COMPARISON_BASIS_AUTHORITY: ComparisonBasisAuthority = {
  ranking: ['budget', 'ramp', 'design'],
};

/** Glass label per basis. The bare word "target" never appears on a tile:
 * it names nothing until the basis is said. */
export const BASIS_LABEL: Readonly<Record<TargetBasis, string>> = {
  design: 'Design',
  ramp: 'Ramp',
  budget: 'Budget',
  stretch: 'Stretch',
  forecast: 'Forecast',
};

export type ComparisonBasisRead = {
  basis: TargetBasis;
  value: number;
  /** `BASIS_LABEL[basis]` — the word beside the figure ("vs Ramp 29.0"). */
  label: string;
  provenance: string;
  /** The design point when a stronger basis won — carried as a secondary
   * figure with its own muted delta, never as a second verdict (verify G3:
   * one number, one verdict per viewport). `undefined` when design won or
   * no design point exists. */
  design?: number;
  /** Bases present for the subject but outranked, strongest first. */
  outranked: readonly TargetBasis[];
};

type Candidate = { basis: TargetBasis; value: number; provenance: string };

function rank(basis: TargetBasis, authority: ComparisonBasisAuthority): number {
  return authority.ranking.indexOf(basis);
}

function pick(candidates: readonly Candidate[], authority: ComparisonBasisAuthority): ComparisonBasisRead | null {
  const ranked = candidates
    .filter((c) => rank(c.basis, authority) !== -1)
    .sort((a, b) => rank(a.basis, authority) - rank(b.basis, authority));
  const winner = ranked[0];
  if (!winner) return null;
  const design = candidates.find((c) => c.basis === 'design');
  return {
    basis: winner.basis,
    value: winner.value,
    label: BASIS_LABEL[winner.basis],
    provenance: winner.provenance,
    design: winner.basis === 'design' ? undefined : design?.value,
    outranked: ranked.slice(1).map((c) => c.basis),
  };
}

/** Resolve the basis for one KPI at one period from `Target` rows.
 * `null` when no ranked basis has a row for that KPI and period. */
export function resolveComparisonBasis(
  kpiId: string,
  period: KpiPeriod,
  targets: readonly Target[],
  authority: ComparisonBasisAuthority = COMPARISON_BASIS_AUTHORITY,
): ComparisonBasisRead | null {
  return pick(
    targets.filter((t) => t.kpiId === kpiId && t.period === period),
    authority,
  );
}

/** The same resolution over a `Reading`'s own `target` / `design` points.
 * A `target` without `targetBasis` is a ramp target — the seed's convention. */
export function readingComparisonBasis(
  reading: Reading,
  authority: ComparisonBasisAuthority = COMPARISON_BASIS_AUTHORITY,
): ComparisonBasisRead | null {
  const candidates: Candidate[] = [];
  if (reading.target !== undefined) {
    candidates.push({
      basis: reading.targetBasis ?? 'ramp',
      value: reading.target,
      provenance: reading.provenanceByField?.target ?? reading.provenance,
    });
  }
  if (reading.design !== undefined) {
    candidates.push({
      basis: 'design',
      value: reading.design,
      provenance: reading.provenanceByField?.design ?? reading.provenance,
    });
  }
  return pick(candidates, authority);
}

/** Signed deviation of `actual` from the resolved basis, in percent of the
 * basis. Positive means above the basis; the caller applies
 * `higherIsBetter` to turn it into a tone. */
export function deviationFromBasisPct(actual: number, read: ComparisonBasisRead): number {
  if (read.value === 0) return 0;
  return ((actual - read.value) / read.value) * 100;
}
